import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Fetch } from '../../utils/Utils.js';
import DataCollection from '../../shared/DataCollection';
import HerdLotCardList from './HerdLotCardList.js';
import '../../shared/DefaultBadge.css';
import '../../weight/lots/WeightLots.css';

const formatHerdLotId = (lotId, prefix) =>
  lotId != null ? `${prefix}-${String(lotId).padStart(4, '0')}` : '-';

export const ListHerdLots = ({ refreshKey, onDefaultSet }) => {
  const { t }    = useTranslation();
  const navigate = useNavigate();
  const [defaultLotId,    setDefaultLotId]    = useState(null);
  const [animalCounts,    setAnimalCounts]    = useState({});
  const [protocols,       setProtocols]       = useState([]);

  useEffect(() => {
    Fetch('api/data/lots.animals?search_fields=type&search_values=herd', 'GET', null,
      (data) => {
        const counts = {};
        (data || []).forEach(row => {
          counts[row.lot_id] = (counts[row.lot_id] || 0) + 1;
        });
        setAnimalCounts(counts);
      },
      () => {}
    );
    Fetch('api/data/lots.plans', 'GET', null,
      (data) => setProtocols(data || []),
      () => {}
    );
  }, [refreshKey]);

  useEffect(() => {
    Fetch('api/ranch', 'GET', null,
      (data) => { if (data?.default_herd_lot) setDefaultLotId(data.default_herd_lot); },
      () => {}
    );
  }, []);

  const getProtocolName = (protocolId) => {
    if (!protocolId) return '—';
    const p = protocols.find(p => p._id === protocolId || p._id?.$oid === protocolId);
    return p ? p.name : '—';
  };

  const handleSetDefault = (lot) => {
    Fetch('api/ranch', 'PATCH', JSON.stringify({ default_herd_lot: lot.lot_id }),
      () => {
        setDefaultLotId(lot.lot_id);
        onDefaultSet?.(lot);
      },
      () => {}
    );
  };

  const columns = [
    {
      name: t('herd_lot_code'),
      selector: row => row.lot_id,
      sortable: true,
      cell: row => (
        <span className="wl-lot-link" onClick={() => navigate(`/herd/lots/${row.lot_id}`)}>
          {formatHerdLotId(row.lot_id, t('herd_lot_prefix'))}
        </span>
      ),
    },
    {
      name: t('herd_lot_name'),
      selector: row => row.name || '-',
      sortable: true,
    },
    {
      name: t('tagged_animals'),
      selector: row => animalCounts[row.lot_id] || 0,
      sortable: true,
    },
    {
      name: t('lot_count'),
      selector: row => row.custom?.animal_count ?? null,
      sortable: true,
      cell: row => row.custom?.animal_count != null ? row.custom.animal_count : '—',
    },
    {
      name: t('plan_name'),
      selector: row => getProtocolName(row.protocol_id),
      sortable: true,
    },
    {
      name: t('default'),
      width: '150px',
      cell: row => row.lot_id === defaultLotId
        ? <span className="default-badge">★ {t('default')}</span>
        : <button className="set-default-btn" onClick={() => handleSetDefault(row)}>{t('set_as_default')}</button>,
    },
  ];

  return (
    <DataCollection
      collection="lots"
      searchFields={{ type: 'herd' }}
      columns={columns}
      sortField={2}
      refreshKey={refreshKey}
      mobileComponent={(data) => (
        <HerdLotCardList
          data={data}
          animalCounts={animalCounts}
          defaultLotId={defaultLotId}
          onSetDefault={handleSetDefault}
          getProtocolName={getProtocolName}
        />
      )}
    />
  );
};

export default ListHerdLots;
